/**
 * Pure logic for the "build your own box" bundle builder.
 * No DOM/window.location/sessionStorage access here — everything takes plain
 * values and returns plain values so it can be reasoned about (and tested)
 * without a live page. DOM wiring and rendering live in recharge-build-your-own.js.
 */
(function() {
  'use strict';

  function toInt(value, fallbackValue) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallbackValue;
  }

  function parsePositiveInt(value) {
    var parsed = toInt(value, 0);
    return parsed > 0 ? parsed : 0;
  }

  function formatMoney(cents, moneyFormat) {
    var amount = (cents || 0) / 100;

    try {
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents || 0, moneyFormat);
      }
    } catch (e) {
      // Fallback below.
    }

    return moneyFormat && moneyFormat.indexOf('{{amount}}') !== -1
      ? moneyFormat.replace('{{amount}}', amount.toFixed(2))
      : '$' + amount.toFixed(2);
  }

  function parseBundleEditItems(rawContents) {
    if (!rawContents) return [];

    return String(rawContents)
      .split(',')
      .map(function(part) {
        var pieces = String(part || '').split(':');
        var productId = String(pieces[0] || '').trim();
        var variantId = String(pieces[1] || '').trim();
        var quantity = toInt(pieces[2], 0);

        return {
          productId: productId,
          variantId: variantId,
          quantity: quantity
        };
      })
      .filter(function(item) {
        return !!item.productId && !!item.variantId && item.quantity > 0;
      });
  }

  function parseBundleRemoveUpdates(removeUrl) {
    var updates = {};
    if (!removeUrl) return updates;

    var query = String(removeUrl || '');
    var questionIndex = query.indexOf('?');
    if (questionIndex >= 0) {
      query = query.slice(questionIndex + 1);
    }

    var params = new URLSearchParams(query);
    params.forEach(function(value, key) {
      var match = key.match(/^updates\[(.+)\]$/);
      if (!match) return;

      updates[match[1]] = Math.max(0, toInt(value, 0));
    });

    return updates;
  }

  function parseBundleLineKeys(rawLineKeys) {
    if (!rawLineKeys) return [];

    return String(rawLineKeys)
      .split(',')
      .map(function(part) { return String(part || '').trim(); })
      .filter(function(key) { return !!key; });
  }

  function deriveVariantMeta(productTitle, variantTitle) {
    var meta = String(variantTitle || '').trim();
    if (!meta || meta === 'Default Title') return '';

    var normalizedProductTitle = String(productTitle || '').trim();
    if (normalizedProductTitle) {
      meta = meta.replace(normalizedProductTitle, '').trim();
    }

    if (meta.indexOf('|') !== -1) {
      meta = meta.split('|')[0].trim();
    }

    if (meta.indexOf('(') !== -1 && meta.indexOf(')') !== -1) {
      meta = meta.split(')')[0].trim() + ')';
    }

    if (meta.length > 1 && meta.charAt(0) === '(' && meta.charAt(meta.length - 1) === ')') {
      meta = meta.slice(1, -1).trim();
    }

    return meta.replace(/\s{2,}/g, ' ').trim();
  }

  function parseSellingPlanPrices(raw) {
    var map = {};
    if (!raw) return map;

    String(raw)
      .split(',')
      .forEach(function(part) {
        var bits = String(part || '').split(':');
        var planId = String(bits[0] || '').trim();
        var planPrice = toInt(bits[1], -1);
        if (!planId || planPrice < 0) return;
        map[planId] = planPrice;
      });

    return map;
  }

  function variantSupportsSellingPlan(variantPlanIds, selectedPlanId) {
    if (!selectedPlanId) return false;
    var planIds = String(variantPlanIds || '');
    if (!planIds) return false;
    return (',' + planIds + ',').indexOf(',' + String(selectedPlanId) + ',') !== -1;
  }

  function computeCardQuantityRules(productRequiredQtyMultiple, productMinOrderQty, variantRequiredQtyMultiple, variantMinOrderQty) {
    var productMultiple = parsePositiveInt(productRequiredQtyMultiple);
    var productMinQty = parsePositiveInt(productMinOrderQty);
    var variantMultiple = parsePositiveInt(variantRequiredQtyMultiple);
    var variantMinQty = parsePositiveInt(variantMinOrderQty);

    return {
      requiredQtyMultiple: variantMultiple || productMultiple || 0,
      minOrderQty: variantMinQty || productMinQty || 0
    };
  }

  function normalizeCardQuantity(requestedValue, rules, options) {
    var settings = options || {};
    var action = settings.action || 'set';
    var value = Math.max(0, toInt(requestedValue, 0));
    var step = Math.max(1, parsePositiveInt(rules && rules.requiredQtyMultiple));
    var minQty = Math.max(0, parsePositiveInt(rules && rules.minOrderQty));

    if (action === 'remove') {
      return 0;
    }

    if (action === 'increase') {
      if (value <= 0) {
        return minQty > 0 ? minQty : step;
      }

      return value + step;
    }

    if (action === 'decrease') {
      if (value <= 0) return 0;
      if (minQty > 0 && value <= minQty) return 0;

      var decreased = value - step;
      if (decreased <= 0) return 0;
      if (minQty > 0 && decreased < minQty) return minQty;
      return decreased;
    }

    if (value <= 0) {
      return 0;
    }

    if (minQty > 0 && value < minQty) {
      value = minQty;
    }

    if (step > 1) {
      if (minQty > 0) {
        if (value <= minQty) {
          value = minQty;
        } else {
          value = minQty + (Math.ceil((value - minQty) / step) * step);
        }
      } else {
        value = Math.ceil(value / step) * step;
      }
    }

    return value;
  }

  function computeSummary(input) {
    var settings = input || {};
    var rows = settings.rows || [];
    var minimumItems = Math.max(0, toInt(settings.minimumItems, 0));
    var freeShippingThresholdCents = Math.max(0, toInt(settings.freeShippingThresholdCents, 0));
    var savingsPercent = Math.max(0, toInt(settings.savingsPercent, 0));
    var isSubscription = !!settings.isSubscription;
    var moneyFormat = settings.moneyFormat;

    var itemCount = rows.reduce(function(total, row) { return total + row.qty; }, 0);
    var subtotal = rows.reduce(function(total, row) { return total + (row.price * row.qty); }, 0);
    var discountCents = isSubscription && savingsPercent > 0
      ? Math.round(subtotal * (savingsPercent / 100))
      : 0;
    var effectiveSubtotal = Math.max(0, subtotal - discountCents);
    var remainingItems = Math.max(0, minimumItems - itemCount);
    var shippingThreshold = freeShippingThresholdCents;
    var shippingRemaining = Math.max(0, shippingThreshold - effectiveSubtotal);
    var shippingProgress = shippingThreshold > 0 ? Math.min(100, (effectiveSubtotal / shippingThreshold) * 100) : 100;
    var progressCopy = shippingRemaining === 0
      ? 'Free shipping unlocked'
      : 'Add ' + formatMoney(shippingRemaining, moneyFormat) + ' more to unlock free shipping';
    var discountCopy = discountCents > 0
      ? 'Recurring savings (' + savingsPercent + '%): -' + formatMoney(discountCents, moneyFormat)
      : '';
    var titleCopy = remainingItems === 0
      ? 'Your box is ready to continue to cart.'
      : 'Add ' + remainingItems + ' or more item' + (remainingItems === 1 ? '' : 's') + ' to your box.';

    return {
      itemCount: itemCount,
      subtotal: subtotal,
      discountCents: discountCents,
      effectiveSubtotal: effectiveSubtotal,
      remainingItems: remainingItems,
      shippingThreshold: shippingThreshold,
      shippingRemaining: shippingRemaining,
      shippingProgress: shippingProgress,
      progressCopy: progressCopy,
      discountCopy: discountCopy,
      titleCopy: titleCopy,
      meetsMinimum: itemCount >= minimumItems
    };
  }

  function computePricingForPlan(rows, planId) {
    var regularTotal = 0;
    var optionTotal = 0;
    var eligible = true;
    var targetPlanId = String(planId || '');

    (rows || []).forEach(function(row) {
      var regularPrice = toInt(row.price, 0);
      var qty = row.qty;
      regularTotal += regularPrice * qty;

      if (!targetPlanId) {
        optionTotal += regularPrice * qty;
        return;
      }

      var planPriceMap = parseSellingPlanPrices(row.sellingPlanPrices);
      if (!Object.prototype.hasOwnProperty.call(planPriceMap, targetPlanId)) {
        eligible = false;
        return;
      }

      optionTotal += toInt(planPriceMap[targetPlanId], regularPrice) * qty;
    });

    return {
      regularTotal: regularTotal,
      optionTotal: optionTotal,
      savings: Math.max(0, regularTotal - optionTotal),
      eligible: eligible || !targetPlanId
    };
  }

  window.LobelsRB = window.LobelsRB || {};
  window.LobelsRB.Logic = {
    toInt: toInt,
    parsePositiveInt: parsePositiveInt,
    formatMoney: formatMoney,
    parseBundleEditItems: parseBundleEditItems,
    parseBundleRemoveUpdates: parseBundleRemoveUpdates,
    parseBundleLineKeys: parseBundleLineKeys,
    deriveVariantMeta: deriveVariantMeta,
    parseSellingPlanPrices: parseSellingPlanPrices,
    variantSupportsSellingPlan: variantSupportsSellingPlan,
    computeCardQuantityRules: computeCardQuantityRules,
    normalizeCardQuantity: normalizeCardQuantity,
    computeSummary: computeSummary,
    computePricingForPlan: computePricingForPlan
  };
})();
